<form class="flex flex-col gap-6" (submit.prevent)="onSubmit()" autocomplete="off">
  <div class="flex items-center">
    <div class="flex flex-col flex-1">
      <h3 class="text-on-surface dark:text-on-surface-darkmode font-semibold">
        {{ 'cockpit.energyInterfaces.details.general.sectionTitle' | transloco }}
      </h3>
      <p class="text-sm text-on-surface-variant dark:text-on-surface-variant-darkmode">
        {{ 'cockpit.energyInterfaces.details.general.sectionSubtitle' | transloco }}
      </p>
    </div>
    <div class="flex-none flex gap-2 items-center">
      @if (!enableEdit()) {
        <ui-button
          mode="outlined"
          class="hidden sm:inline"
          data-track="cockpit-energy-interface-edit-button"
          (click)="onClickEnableEdit()"
          [loading]="editButtonLoading()"
          type="button"
        >
          <ng-icon name="heroPencilSquare" size="20" slot="start" class="!block" />
          {{ 'cockpit.energyInterfaces.details.general.editButton' | transloco }}
        </ui-button>
        <ui-button
          mode="outlined"
          class="sm:hidden"
          [iconOnly]="true"
          data-track="cockpit-energy-interface-edit-button-mobile"
          [ariaLabel]="'cockpit.energyInterfaces.details.general.editButton' | transloco"
          (click)="onClickEnableEdit()"
          [loading]="editButtonLoading()"
          type="button"
        >
          <ng-icon name="heroPencilSquare" size="20" class="!block" />
        </ui-button>
      } @else {
        <ui-button type="button" mode="outlined" (click)="onClickCancel()">
          {{ 'cockpit.energyInterfaces.details.general.cancelButton' | transloco }}
        </ui-button>
        <ui-button
          type="submit"
          [disabled]="!form().touched() || form().invalid()"
          [loading]="isUpdateLoading()"
        >
          <ng-icon name="heroCheck" size="20" slot="start" class="!block" />
          {{ 'cockpit.energyInterfaces.details.general.saveButton' | transloco }}
        </ui-button>
      }
    </div>
  </div>
  <div
    class="grid grid-cols-1 sm:grid-cols-2 items-center border-t border-surface-highlight dark:border-surface-highlight-darkmode"
  >
    <!-- label -->
    <label
      for="edit-energy-interface-label"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      {{ 'cockpit.energyInterfaces.details.general.labelLabel' | transloco }}
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        {{ model().label }}
      </span>
      <ui-input-simple
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-label"
        [formField]="form.label"
      />
      <app-field-errors [fieldState]="form.label()" class="absolute" />
    </div>
    <!-- status -->
    <label
      for="edit-energy-interface-locationID"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      <span class="flex items-center gap-0.5">
        {{ 'cockpit.energyInterfaces.details.general.statusLabel' | transloco }}

        <div class="flex-none font-normal text-sm">
          <ui-button
            [iconOnly]="true"
            mode="ghost"
            size="small"
            type="button"
            (click)="statusHelpDialog.showModal()"
          >
            <span class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode">
              <ng-icon name="heroQuestionMarkCircleSolid" size="20" class="!block" />
            </span>
          </ui-button>

          <ui-simple-dialog #statusHelpDialog>
            <ui-simple-dialog-layout (clickCancel)="statusHelpDialog.close()">
              <h3 slot="title" class="font-semibold">
                {{ 'help.energyInterfaceStatus.title' | transloco }}
              </h3>
              <ul class="flex flex-col gap-5">
                <li class="flex flex-col gap-2">
                  <ui-badge color="warning">{{
                    'cockpit.energyInterfaces.details.general.statusIncomplete' | transloco
                  }}</ui-badge>
                  <p>
                    {{ 'help.energyInterfaceStatus.statusIncompleteDescription' | transloco }}
                  </p>
                </li>
                <li class="flex flex-col gap-2">
                  <ui-badge color="soft">{{
                    'cockpit.energyInterfaces.details.general.statusUnused' | transloco
                  }}</ui-badge>
                  <p>
                    {{ 'help.energyInterfaceStatus.statusUnusedDescription' | transloco }}
                  </p>
                </li>
                <li class="flex flex-col gap-2">
                  <ui-badge color="success">{{
                    'cockpit.energyInterfaces.details.general.statusActive' | transloco
                  }}</ui-badge>
                  <p>
                    {{ 'help.energyInterfaceStatus.statusActiveDescription' | transloco }}
                  </p>
                </li>
              </ul>
            </ui-simple-dialog-layout>
          </ui-simple-dialog>
        </div>
      </span>
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        @switch (energyInterfaceStatus()) {
          @case ('INCOMPLETE') {
            <div
              class="px-2 py-1 text-xs rounded-sm border border-warning-outline dark:border-warning-outline-darkmode bg-warning-soft dark:bg-warning-soft-darkmode text-on-warning-soft dark:text-on-warning-soft-darkmode"
            >
              {{ 'cockpit.energyInterfaces.details.general.statusIncomplete' | transloco }}
            </div>
          }
          @case ('ATTACHED') {
            <div
              class="px-2 py-1 text-xs rounded-sm border border-success-outline dark:border-success-outline-darkmode bg-success-soft dark:bg-success-soft-darkmode text-on-success-soft dark:text-on-success-soft-darkmode"
            >
              {{ 'cockpit.energyInterfaces.details.general.statusActive' | transloco }}
            </div>
          }
          @case ('UNATTACHED') {
            <div
              class="px-2 py-1 text-xs rounded-sm border border-outline dark:border-outline-darkmode bg-surface-magic dark:bg-surface-magic-darkmode text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
            >
              {{ 'cockpit.energyInterfaces.details.general.statusUnused' | transloco }}
            </div>
          }
          @default {
            <div
              class="px-2 py-1 text-xs rounded-sm border border-outline dark:border-outline-darkmode bg-surface-magic dark:bg-surface-magic-darkmode text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
            >
              {{ 'cockpit.energyInterfaces.details.general.statusUnknown' | transloco }}
            </div>
          }
        }
      </span>
    </div>
    <!-- utilityType -->
    <label
      for="edit-energy-interface-utilityType"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      {{ 'cockpit.energyInterfaces.details.general.utilityTypeLabel' | transloco }}
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        <ng-icon
          [name]="model().utilityType === 'ELECTRICITY' ? 'heroBolt' : 'heroFire'"
          size="20"
          class="!block"
        />
        {{
          (model().utilityType === 'ELECTRICITY'
            ? 'cockpit.energyInterfaces.details.general.utilityTypeElectricity'
            : 'cockpit.energyInterfaces.details.general.utilityTypeGas'
          ) | transloco
        }}
      </span>
      <ui-select
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-utilityType"
        [formField]="form.utilityType"
      >
        <option value="ELECTRICITY" [selected]="model().utilityType === 'ELECTRICITY'">
          {{ 'cockpit.energyInterfaces.details.general.utilityTypeElectricity' | transloco }}
        </option>
        <option value="GAS" [selected]="model().utilityType === 'GAS'">
          {{ 'cockpit.energyInterfaces.details.general.utilityTypeGas' | transloco }}
        </option>
      </ui-select>
      <app-field-errors [fieldState]="form.utilityType()" class="absolute" />
    </div>
    <!-- measurementType -->
    <label
      for="edit-energy-interface-measurementType"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      <span class="flex items-center gap-0.5">
        {{ 'cockpit.energyInterfaces.details.general.measurementTypeLabel' | transloco }}

        <ui-help-button class="flex-none font-normal text-sm">
          <h3 slot="title" class="font-semibold">
            {{ 'help.measurementType.title' | transloco }}
          </h3>
          <p>
            {{ 'help.measurementType.description' | transloco }}
          </p>
        </ui-help-button>
      </span>
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        @switch (model().measurementType) {
          @case ('RLM') {
            {{ 'cockpit.energyInterfaces.details.general.measurementTypeRLM' | transloco }}
          }
          @case ('SLP') {
            {{ 'cockpit.energyInterfaces.details.general.measurementTypeSLP' | transloco }}
          }
        }

        @switch (model().flowDirection) {
          @case ('CONSUMPTION') {
            ({{ 'cockpit.energyInterfaces.details.general.flowDirectionConsumption' | transloco }})
          }
          @case ('FEED_IN') {
            ({{ 'cockpit.energyInterfaces.details.general.flowDirectionFeedIn' | transloco }})
          }
        }
      </span>
      <ui-select
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-measurementType"
        [formField]="form.measurementType"
      >
        <option value="RLM" [selected]="model().measurementType === 'RLM'">
          {{ 'cockpit.energyInterfaces.details.general.measurementTypeRLM' | transloco }}
        </option>
        <option value="SLP" [selected]="model().measurementType === 'SLP'">
          {{ 'cockpit.energyInterfaces.details.general.measurementTypeSLP' | transloco }}
        </option>
      </ui-select>
      <app-field-errors [fieldState]="form.measurementType()" class="absolute" />
    </div>
    <!-- malo -->
    <label
      for="edit-energy-interface-malo"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      <span class="flex items-center gap-0.5">
        {{ 'cockpit.energyInterfaces.details.general.maloIDLabel' | transloco }}

        <ui-help-button class="flex-none font-normal text-sm">
          <h3 slot="title" class="font-semibold">
            {{ 'help.maloID.title' | transloco }}
          </h3>
          <p>
            {{ 'help.maloID.description' | transloco }}
          </p>
          <a
            slot="footer"
            [href]="maloMeloMeterArticleUrl"
            target="_blank"
            [title]="'help.maloID.linkTitle' | transloco"
          >
            <ui-button mode="outlined" type="button" size="small">
              <ng-icon name="heroArrowTopRightOnSquare" size="20" slot="start" class="!block" />
              {{ 'help.maloID.linkText' | transloco }}
            </ui-button>
          </a>
        </ui-help-button>
      </span>
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        {{ model().maloID || '-' }}
      </span>
      <ui-input-simple
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-malo"
        [formField]="form.maloID"
      />
      <app-field-errors [fieldState]="form.maloID()" class="absolute" />
    </div>
    <!-- meter -->
    <label
      for="edit-energy-interface-meter"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      <span class="flex items-center gap-0.5">
        {{ 'cockpit.energyInterfaces.details.general.meterIDLabel' | transloco }}

        <ui-help-button class="flex-none font-normal text-sm">
          <h3 slot="title" class="font-semibold">
            {{ 'help.meterID.title' | transloco }}
          </h3>
          <p>
            {{ 'help.meterID.description' | transloco }}
          </p>
        </ui-help-button>
      </span>
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        {{ model().meterID || '-' }}
      </span>
      <ui-input-simple
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-meter"
        [formField]="form.meterID"
      />
      <app-field-errors [fieldState]="form.meterID()" class="absolute" />
    </div>
    <!-- locationID -->
    <label
      for="edit-energy-interface-locationID"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      {{ 'cockpit.energyInterfaces.details.general.locationLabel' | transloco }}
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        [class.hidden]="enableEdit()"
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        @if (selectedLocation()?.data; as location) {
          {{
            (location | formatLocationName) ||
              ('cockpit.energyInterfaces.details.general.withoutLocation' | transloco)
          }}
        }
      </span>
      <ui-select
        [class.hidden]="!enableEdit()"
        inputId="edit-energy-interface-locationID"
        [formField]="form.locationID"
      >
        <option value="" [selected]="model().locationID ? null : 'selected'">
          {{ 'cockpit.energyInterfaces.details.general.selectLocation' | transloco }}
        </option>
        @for (location of locationsStore.filteredLocations(); track location.id) {
          <option
            [value]="location.id"
            [selected]="model().locationID === location.id ? 'selected' : null"
          >
            {{ location.data | formatLocationName }}
          </option>
        }
      </ui-select>
      <app-field-errors [fieldState]="form.locationID()" class="absolute" />
    </div>
    <!-- contractID -->
    <label
      for="edit-energy-interface-locationID"
      class="pt-6 sm:py-6 sm:min-h-22 flex items-center mb-[-1px] border-b-none sm:border-b border-surface-highlight dark:border-surface-highlight-darkmode text-on-surface dark:text-on-surface-darkmode text-sm font-semibold leading-6 line-clamp-2"
    >
      {{ 'cockpit.energyInterfaces.details.general.contractLabel' | transloco }}
    </label>
    <div
      class="pb-6 pt-1 sm:py-6 border-b border-surface-highlight dark:border-surface-highlight-darkmode"
    >
      <span
        class="min-h-10 flex items-center sm:px-3 sm:py-1.5 text-on-surface-subtle dark:text-on-surface-subtle-darkmode leading-6 line-clamp-1"
      >
        -
      </span>
    </div>
  </div>
</form>
